<!-- eslint-disable vue/no-v-html -->
<template>
  <article>
    <LoaderStack v-if="isLoading" />
    <section v-show="!isLoading" class="pb-12 pt-4">
      <div class="container-wrap">
        <div v-if="detail && detail.title">
          <div id="advan-resources" class="m-auto w-full max-w-[1200px]">
            <div class="detailpage-top">
              <h1>{{ detail.title }}</h1>
              <p class="mb-8 mt-3 text-sm italic text-black">
                Date: {{ detail.releaseDate }}
              </p>
            </div>
          </div>
          <div
            v-if="!isVideo"
            class="m-auto flex w-full max-w-[1200px] flex-wrap"
          >
            <div v-if="outlines.length > 0" class="element-aside">
              <div class="border-2 border-solid border-gray-100 bg-gray-100">
                <div class="px-5 py-2.5"><strong>Outline</strong></div>
                <ul>
                  <li v-for="item in outlines" :key="item.id">
                    <button
                      :title="item.name"
                      class="block w-full px-5 py-2 text-left transition-colors"
                      :class="[
                        activeOutlineId === item.id
                          ? 'bg-blue-700 font-semibold text-white'
                          : 'bg-white text-blue-700 desktop:hover:bg-blue-700 desktop:hover:text-white',
                      ]"
                      @click="handleScrollTo(item.id)"
                    >
                      {{ item.name }}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            <div
              class="element-article"
              :class="outlines.length <= 0 && 'm-auto'"
            >
              <div id="material-content">
                <div v-html="sanitizedContent" />
              </div>
            </div>
          </div>
          <div
            v-else
            class="relative m-auto w-full max-w-[1200px] overflow-hidden"
          >
            <VideoModel
              :video-title="detail.title"
              :video-url="detail.videoUrl"
              :is-auto-play="true"
            />
          </div>
        </div>
      </div>
    </section>
  </article>
</template>

<script setup lang="ts">
  import { useIntersectionObserver } from '@vueuse/core';
  import sanitizeHtml from 'sanitize-html';
  import { getCurrentInstance, h, render } from 'vue';

  import TheIframe from '@/components/Shared/TheIframe.vue';

  interface IOutline {
    id: string;
    name: string;
  }

  const route = useRoute();
  const { blobDomain, headerHeight, provideWebTitle } = useWebsite();

  const materialStore = useMaterialStore();
  const { detail } = storeToRefs(materialStore);

  const { handleScrollTo } = useScrollTo({ offset: headerHeight.value });

  const { status: GET_RESOURCES_DETAIL_STATUS } =
    await materialStore.USE_GET_RESOURCES_DETAIL(
      route.params.details as string
    );

  const outlines = ref<IOutline[]>([]);
  const activeOutlineId = ref<string>('');
  const observerCleanups = ref<Array<() => void>>([]);
  const iframeContainers = ref<HTMLElement[]>([]);
  const isMenuInited = ref<boolean>(false);

  // 保留目前 app 的 context (Pinia/plugins)，掛載到 v-html 節點的元件才能沿用
  const appContext = getCurrentInstance()?.appContext;

  const isLoading = computed((): boolean => {
    if (isMenuInited.value === false) {
      return true;
    }
    return GET_RESOURCES_DETAIL_STATUS.value === 'pending';
  });

  const isVideo = computed(
    (): boolean =>
      !!(detail.value.videoUrl && detail.value.videoUrl.length >= 1)
  );

  const isSnippeIdExist = computed(
    (): boolean =>
      !!(detail.value.content && detail.value.content.includes('snippe-id'))
  );

  const sanitizedContent = computed((): string =>
    sanitizeContentHTML(detail.value.content ?? '')
  );

  watch(
    [GET_RESOURCES_DETAIL_STATUS],
    (value) => {
      if (value.includes('error')) {
        showError({ statusCode: 404 });
      }
    },
    { immediate: true }
  );

  /**
   * 將 Materials Page 的內容做 sanitize 處理，避免 XSS 攻擊
   */

  const ALLOWED_IFRAME_ORIGINS = [
    'youtu.be',
    'www.youtube.com',
    'www.youtube-nocookie.com',
    'www.bilibili.com',
    'player.bilibili.com',
    'fast.wistia.net',
  ];

  const sanitizeContentHTML = (html: string): string => {
    // NOTE: 因為 iframe 要經過 CookieYes 權限驗證，因此要將 iframe 轉成 div.mount-iframe
    return sanitizeHtml(html, {
      allowedTags: [
        ...sanitizeHtml.defaults.allowedTags,
        'img',
        'iframe',
        'wistia-player',
      ],
      allowedAttributes: {
        '*': ['style', 'class', 'id', 'data-src'],
        a: ['href', 'target', 'title'],
        img: ['src', 'alt', 'width', 'height'],
        iframe: [
          'src',
          'width',
          'height',
          'title',
          'allow',
          'allowfullscreen',
          'frameborder',
          'referrerpolicy',
          'sandbox',
        ],
        'wistia-player': ['media-id'],
      },
      allowedIframeHostnames: [...ALLOWED_IFRAME_ORIGINS],
      transformTags: {
        iframe: (_tagName, attribs) => {
          const src = attribs.src || '';
          const isAllowed = ALLOWED_IFRAME_ORIGINS.some((origin) =>
            src.startsWith(`https://${origin}`)
          );
          if (!isAllowed) {
            return { tagName: 'div', attribs: {} as Record<string, string> };
          }
          return {
            tagName: 'div',
            attribs: {
              'data-src': src,
              class:
                'mount-iframe relative aspect-video w-full overflow-hidden bg-black',
            },
          };
        },
        'wistia-player': (_tagName, attribs) => {
          const mediaId = attribs['media-id'];
          if (!mediaId) {
            return { tagName: 'div', attribs: {} as Record<string, string> };
          }
          return {
            tagName: 'div',
            attribs: {
              'data-src': `https://fast.wistia.net/embed/iframe/${mediaId}`,
              class:
                'mount-iframe relative aspect-video w-full overflow-hidden bg-black',
            },
          };
        },
      },
    });
  };

  /**
   * 將 Mount Iframe 掛載到 TheIframe，讓 iframe 可以經過 CookieYes 權限驗證
   */

  const setMountIframes = (): void => {
    iframeContainers.value.forEach((container) => render(null, container));
    iframeContainers.value = [];

    const nodes = document.querySelectorAll<HTMLElement>(
      '#material-content .mount-iframe[data-src]'
    );

    nodes.forEach((node) => {
      const src = node.dataset.src;
      if (!src) return;

      const vnode = h(TheIframe, { src });
      vnode.appContext = appContext ?? null;
      render(vnode, node);
      iframeContainers.value.push(node);
    });
  };

  /**
   * 將 Materials Page 內的標題抓出來做成側邊選單的錨點連結
   */

  const setMenuContent = (): void => {
    outlines.value = [];
    observerCleanups.value.forEach((cleanup) => cleanup());

    const nodes = document.querySelectorAll('#material-content h2');

    nodes.forEach((node, index) => {
      const name = node.textContent?.trim();
      if (!name) return;

      const id = `target_${index}`;
      node.id = id;
      outlines.value.push({ id, name });

      const { stop } = useIntersectionObserver(
        node as HTMLElement,
        ([entry]: IntersectionObserverEntry[]) => {
          if (entry?.isIntersecting) {
            activeOutlineId.value = entry.target.id;
          }
        },
        {
          rootMargin: `-${headerHeight.value + 20}px 0px -70% 0px`,
          threshold: 0,
        }
      );
      observerCleanups.value.push(stop);
    });

    if (outlines.value.length > 0) {
      activeOutlineId.value = outlines.value[0].id;
    }
  };

  const initializePage = async (): Promise<void> => {
    if (import.meta.client) {
      isMenuInited.value = false;

      await nextTick(() => {
        setMenuContent();
        setMountIframes();
        isMenuInited.value = true;
      });
    }
  };

  watch(
    () => GET_RESOURCES_DETAIL_STATUS.value,
    async (value) => {
      if (value === 'success') {
        await initializePage();
      }
    },
    { immediate: true }
  );

  onMounted(async () => {
    await initializePage();
  });

  onUnmounted(() => {
    observerCleanups.value.forEach((cleanup) => cleanup());
    iframeContainers.value.forEach((container) => render(null, container));
    materialStore.RESET_RESOURCES_DETAIL();
  });

  useSeoMeta({
    title: () => detail.value.title,
    ogTitle: () => provideWebTitle(detail.value.title),
    twitterTitle: () => provideWebTitle(detail.value.title),
    description: () => detail.value.abstract,
    ogDescription: () => detail.value.abstract,
    twitterDescription: () => detail.value.abstract,
  });

  useHead({
    link: () => [
      {
        rel: 'stylesheet',
        href:
          isSnippeIdExist.value && !isVideo.value
            ? `${blobDomain.value}/web/css/cms/content-advantech.css`
            : `${blobDomain.value}/web/lib/ContentBuilder/assets/minimalist-basic/content.css`,
      },
      {
        rel: 'stylesheet',
        href: 'https://www.advantech.com/css/resources_center/advan-resources.css',
      },
    ],
  });
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/_utils.scss' as *;

  .element-aside {
    @apply mb-8 w-full;

    @include min-width(940px) {
      @apply sticky w-[calc(100%-668px)];
      top: calc(var(--header-height, 80px) + 1rem);
      max-height: calc(100vh - var(--header-height, 80px) - 2rem);
      overflow-y: auto;
    }

    @include min-width(1200px) {
      @apply w-[calc(1200px-668px)] max-w-[360px] pr-9;
    }
  }

  .element-article {
    @apply w-full max-w-[668px];
  }
</style>
